import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import React, { useContext, useEffect, useState } from 'react';
import { isAdmin } from '../../common/actionHelper';
import {
  DisputeReason,
  DisputeReasonDisplayMap,
} from '../../common/constants/disputeReasons';
import { HttpService } from '../../common/http.service';
import { AuthContext } from '../login/AuthWrapper';
import {
  getWorkbenchMigrationStatusEndPoint,
  migrateToWorkbenchEndPoint,
} from './api.endPoints';
import { PolicyData } from './dtos/policyData.dto';

interface Props {
  policyData: PolicyData;
}

/** Maps API/catch errors to a safe string for the UI (avoids [object Object] and non-string messages). */
function userFacingErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message: unknown }).message;
    if (typeof m === 'string' && m.length > 0) {
      return m;
    }
  }
  return fallback;
}

export const MoveToWorkbench = ({ policyData }: Props) => {
  const auth = useContext(AuthContext);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reasons, setReasons] = useState<DisputeReason[]>([]);
  const [loading, setLoading] = useState(false);
  const [existingApplicationId, setExistingApplicationId] = useState<
    number | null
  >(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [migrationError, setMigrationError] = useState<string | null>(null);
  const [ineligibilityReason, setIneligibilityReason] = useState<string | null>(
    null,
  );

  useEffect(() => {
    (async () => {
      setCheckingStatus(true);
      setDialogOpen(false);
      setReasons([]);
      setLoading(false);
      setExistingApplicationId(null);
      setIneligibilityReason(null);
      setMigrationError(null);
      try {
        const data = await HttpService.getInstance().get(
          getWorkbenchMigrationStatusEndPoint(policyData.policyId),
        );
        if (data && !data.error) {
          const { applicationId, ineligibilityReason: reason } =
            data.parsedBody ?? {};
          if (applicationId) {
            setExistingApplicationId(applicationId);
          }
          if (reason) {
            setIneligibilityReason(reason);
          }
        }
      } catch {
        // status check failed — degrade gracefully, show the button as enabled
      } finally {
        setCheckingStatus(false);
      }
    })();
  }, [policyData.policyId]);

  if (!isAdmin(auth.roles) || checkingStatus) {
    return null;
  }

  const workbenchUrl = existingApplicationId
    ? `/workbench/applications/${existingApplicationId}`
    : null;

  const handleOpen = () => {
    if (ineligibilityReason) {
      setMigrationError(ineligibilityReason);
      return;
    }
    setMigrationError(null);
    setReasons([]);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const handleMigrate = async () => {
    if (reasons.length === 0) return;
    setLoading(true);
    try {
      const data = await HttpService.getInstance().post(
        migrateToWorkbenchEndPoint(),
        {
          termPolicyId: policyData.policyId,
          carrier: policyData.carrier,
          product: policyData.product,
          reasons,
        },
      );
      if (data && !data.error) {
        const { applicationId } = data.parsedBody ?? {};
        if (applicationId != null) {
          setExistingApplicationId(applicationId);
        }
        setDialogOpen(false);
      } else {
        setMigrationError(
          userFacingErrorMessage(
            data?.error,
            'Failed to move case to Workbench.',
          ),
        );
      }
    } catch (e: unknown) {
      setMigrationError(
        userFacingErrorMessage(e, 'Failed to move case to Workbench.'),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box mt={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="contained"
            size="small"
            onClick={handleOpen}
            disabled={!!workbenchUrl}
          >
            Move case to Workbench
          </Button>
          {workbenchUrl && (
            <Button
              variant="text"
              size="small"
              href={workbenchUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open case in Workbench
            </Button>
          )}
        </Box>
        {migrationError && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {migrationError}
          </Typography>
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={handleClose} fullWidth maxWidth="xs">
        <DialogTitle>Move Case to Workbench</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Policy ID: {policyData.policyId}
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="dispute-reason-label">Select reason(s)</InputLabel>
            <Select
              labelId="dispute-reason-label"
              multiple
              value={reasons}
              onChange={e => setReasons(e.target.value as DisputeReason[])}
              input={<OutlinedInput label="Select reason(s)" />}
              renderValue={selected =>
                (selected as DisputeReason[])
                  .map(r => DisputeReasonDisplayMap[r])
                  .join(', ')
              }
            >
              {Object.values(DisputeReason).map(r => (
                <MenuItem key={r} value={r}>
                  <Checkbox checked={reasons.includes(r)} size="small" />
                  <ListItemText primary={DisputeReasonDisplayMap[r]} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleMigrate}
            variant="contained"
            disabled={reasons.length === 0 || loading}
          >
            {loading ? 'Moving…' : 'Move to Workbench'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
